import {
  type BulkMessageStatisticDto,
  type DashboardSettingsResponseDto,
  type Message,
  type MessageRequestDto,
  type MessageResponseDto,
  type PpvFollowUpMessageResponseDto,
  PpvFollowUpMessagesRestControllerService,
} from '@api'
import { useUnit } from 'effector-react'
import { useMutation, useQueryClient } from 'react-query'

import { $creatorId } from '@/models/creators.ts'
import { useFollowUpMessageService } from '@/modules/creator-settings/follow-up-messages/service/follow-up-message-fetch-context'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider'
import { handleError } from '@/utils/error'

export function useUpdateFollowUpMessageToggle() {
  const client = useQueryClient()
  const creatorId = useUnit($creatorId)
  const { settingsSortOptions } = useSettingsContext()
  const { updateToggle } = useFollowUpMessageService()

  return useMutation(
    (toggle: boolean) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }

      return updateToggle(creatorId, toggle)
    },
    {
      onMutate: (enabled) => {
        client.setQueryData<DashboardSettingsResponseDto | undefined>(
          ['settings', creatorId, settingsSortOptions],
          (state) => {
            if (!state) {
              return state
            }

            return { ...state, ppvFollowUpsEnabled: enabled }
          },
        )
      },
      onError: (error, toggle) => {
        handleError(
          error,
          toggle
            ? 'Something went wrong while enabling follow up message'
            : 'Something went wrong while disabling follow up message',
        )
        client.invalidateQueries(['settings', creatorId, settingsSortOptions])
      },
    },
  )
}

export function useUpdateSendPurchasedPPVFollowUps() {
  const client = useQueryClient()
  const creatorId = useUnit($creatorId)
  const { settingsSortOptions } = useSettingsContext()

  return useMutation(
    (toggle: boolean) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }

      return PpvFollowUpMessagesRestControllerService.apiToggleFollowUpMessagesBoughtNotReplied(
        {
          creatorId,
          requestBody: { state: toggle },
        },
      )
    },
    {
      onMutate: (enabled) => {
        client.setQueryData<DashboardSettingsResponseDto | undefined>(
          ['settings', creatorId, settingsSortOptions],
          (state) => {
            if (!state) {
              return state
            }

            return { ...state, ppvFollowUpsBoughtNotReplied: enabled }
          },
        )
      },
      onError: (error, toggle) => {
        handleError(
          error,
          toggle
            ? 'Something went wrong while enabling send purchased ppv follow ups'
            : 'Something went wrong while disabling send purchased ppv follow ups',
        )
        client.invalidateQueries(['settings', creatorId, settingsSortOptions])
      },
    },
  )
}

export function useUpdateFollowUpMessageNotSeenToggle() {
  const client = useQueryClient()
  const { settingsSortOptions } = useSettingsContext()
  const creatorId = useUnit($creatorId)
  const { updateNotSeenToggle } = useFollowUpMessageService()

  return useMutation(
    (toggle: boolean) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }

      return updateNotSeenToggle(creatorId, toggle)
    },
    {
      onMutate: (enabled) => {
        client.setQueryData<DashboardSettingsResponseDto | undefined>(
          ['settings', creatorId, settingsSortOptions],
          (state) => {
            if (!state) {
              return state
            }

            return { ...state, ppvFollowUpsNotSeen: enabled }
          },
        )
      },
      onError: (error, toggle) => {
        handleError(
          error,
          toggle
            ? 'Something went wrong while enabling follow up message not seen'
            : 'Something went wrong while disabling follow up message not seen',
        )
        client.invalidateQueries(['settings', creatorId, settingsSortOptions])
      },
    },
  )
}

export function useUpdateFollowUpMessageDelay() {
  const client = useQueryClient()
  const { settingsSortOptions } = useSettingsContext()
  const creatorId = useUnit($creatorId)
  const { updateDelay } = useFollowUpMessageService()

  return useMutation(
    (minutes: number) => {
      return updateDelay(creatorId!, minutes)
    },
    {
      onMutate: (minutes) => {
        client.setQueryData<DashboardSettingsResponseDto | undefined>(
          ['settings', creatorId, settingsSortOptions],
          (state) => {
            if (!state) {
              return state
            }

            return { ...state, sendPpvFollowUpsAfterMinutes: minutes }
          },
        )
      },
      onError: (error, minutes) => {
        handleError(
          error,
          `Something went wrong while updating follow up message delay to ${minutes} minutes`,
        )
        client.invalidateQueries(['settings', creatorId, settingsSortOptions])
      },
    },
  )
}

export function useUpdateFollowUpMessage() {
  const client = useQueryClient()
  const { settingsSortOptions } = useSettingsContext()
  const creatorId = useUnit($creatorId)

  return useMutation(
    (message: Message) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }

      return PpvFollowUpMessagesRestControllerService.editFollowUpMessage({
        creatorId: creatorId!,
        id: message.id!,
        requestBody: {
          message: message as MessageRequestDto,
        },
      })
    },
    {
      onMutate: (message) => {
        client.setQueryData<DashboardSettingsResponseDto | undefined>(
          ['settings', creatorId, settingsSortOptions],
          (state) => {
            if (!state) {
              return state
            }
            const sequences = state.ppvFollowUpMessages ?? []
            const newFollowUpMessages = sequences.map(
              ({ sequence, ...rest }): PpvFollowUpMessageResponseDto => {
                return {
                  ...rest,
                  sequence: sequence
                    ? {
                        ...sequence,
                        messages:
                          sequence.messages?.map((it) =>
                            it.id === message.id
                              ? (message as MessageResponseDto)
                              : it,
                          ) ?? [],
                      }
                    : undefined,
                }
              },
            )

            return { ...state, ppvFollowUpMessages: newFollowUpMessages }
          },
        )
      },
      onSettled: () => {
        client.invalidateQueries(['settings', creatorId, settingsSortOptions])
      },
      onError: (error) => {
        handleError(
          error,
          'Something went wrong while updating follow up message',
        )
      },
    },
  )
}

const defaultMessage: MessageResponseDto & BulkMessageStatisticDto = {
  id: 0,
  enableFallbackVariable: false,
  price: 0,
  sorting: 0,
  fallback: '',
  text: '',
  medias: [],
  fallbackVariable: '',
  totalSent: 0,
  totalReads: 0,
  totalResponses: 0,
  totalPPVBought: 0,
}

export function useCreateFollowUpMessage() {
  const client = useQueryClient()
  const { settingsSortOptions } = useSettingsContext()
  const creatorId = useUnit($creatorId)

  return useMutation(
    (message: MessageRequestDto) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }

      return PpvFollowUpMessagesRestControllerService.addFollowUpMessage({
        creatorId,
        requestBody: {
          message: message as MessageRequestDto,
        },
      })
    },
    {
      onMutate: (message) => {
        client.setQueryData<DashboardSettingsResponseDto | undefined>(
          ['settings', creatorId, settingsSortOptions],
          // @ts-ignore
          (state) => {
            if (!state) {
              return state
            }
            const sequences = state.ppvFollowUpMessages ?? []

            if (sequences.length === 0) {
              return {
                ...state,
                ppvFollowUpMessages: [
                  {
                    id: 0,
                    sequence: {
                      id: 0,
                      messages: [
                        {
                          ...defaultMessage,
                          ...message,
                        },
                      ],
                      sequenceKey: 'default',
                    },
                  },
                ],
              }
            }
            const [{ sequence, ...firstSequenceRest }, ...otherSequences] =
              sequences
            const newFollowUpMessages: PpvFollowUpMessageResponseDto[] = [
              {
                ...firstSequenceRest,
                sequence: sequence
                  ? {
                      ...sequence,
                      messages: [
                        ...(sequence?.messages ?? []),
                        { ...defaultMessage, ...message },
                      ],
                    }
                  : undefined,
              },
              ...otherSequences,
            ]

            return { ...state, ppvFollowUpMessages: newFollowUpMessages }
          },
        )
      },
      onSettled: () => {
        client.invalidateQueries(['settings', creatorId, settingsSortOptions])
      },
      onError: (error) => {
        handleError(
          error,
          'Something went wrong while creating follow up message',
        )
      },
    },
  )
}

export function useDeleteFollowUpMessage() {
  const client = useQueryClient()
  const { settingsSortOptions } = useSettingsContext()
  const creatorId = useUnit($creatorId)
  const { deleteMessage } = useFollowUpMessageService()

  return useMutation(
    (messageId: number) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }

      return deleteMessage(creatorId, messageId)
    },
    {
      onMutate: (messageId) => {
        client.setQueryData<DashboardSettingsResponseDto | undefined>(
          ['settings', creatorId, settingsSortOptions],
          (state) => {
            if (!state) {
              return state
            }
            const sequences = state.ppvFollowUpMessages ?? []
            const newFollowUpMessages = sequences.map(
              ({ sequence, ...rest }) => {
                return {
                  ...rest,
                  sequence: sequence
                    ? {
                        ...sequence,
                        messages:
                          sequence?.messages?.filter(
                            (it) => it.id !== messageId,
                          ) ?? [],
                      }
                    : undefined,
                }
              },
            )

            return {
              ...state,
              ppvFollowUpMessages: newFollowUpMessages,
            }
          },
        )
      },
      onSettled: () => {
        client.invalidateQueries(['settings', creatorId, settingsSortOptions])
      },
      onError: (error) => {
        handleError(
          error,
          'Something went wrong while deleting follow up message',
        )
      },
    },
  )
}
